/**
 * /api/v1/cart — the customer's list of files and how each one prints.
 *
 * THE SERVER HOLDS THE CART, and that is the whole design. Every call here
 * answers with the complete `CartOut`, so this module keeps no state of its
 * own: a mutation's response simply becomes the cache, which means there is
 * no local copy to drift, no patch to merge, and no way for a slow response
 * to repaint a price for a cart that has since changed.
 *
 * The one thing the client still owns is a file whose bytes are still moving.
 * That has no id yet, so it cannot be a cart row — see `useUploadQueue`.
 */
import { api } from './client';
import { queryKeys, useApiQuery } from './queryClient';
import type {
  QuoteOut,
  CartAddRequest,
  CartBulkResultOut,
  CartBulkUpdate,
  CartItemUpdate,
  CartOut,
} from './types';

export const CART_PATH = '/api/v1/cart';

export function fetchCart(signal?: AbortSignal): Promise<CartOut> {
  return api.get<CartOut>(CART_PATH, signal ? { signal } : undefined);
}

export function addToCart(uploadId: string, signal?: AbortSignal): Promise<CartOut> {
  const body: CartAddRequest = { upload_id: uploadId };
  return api.post<CartOut>(`${CART_PATH}/items`, body, signal ? { signal } : undefined);
}

export function updateCartItem(
  itemId: string,
  patch: CartItemUpdate,
  signal?: AbortSignal,
): Promise<CartOut> {
  return api.patch<CartOut>(`${CART_PATH}/items/${itemId}`, patch, signal ? { signal } : undefined);
}

export function removeCartItem(itemId: string, signal?: AbortSignal): Promise<CartOut> {
  return api.delete<CartOut>(`${CART_PATH}/items/${itemId}`, signal ? { signal } : undefined);
}

/**
 * Apply one set of choices to every file.
 *
 * Returns what it MANAGED, not just the cart: a binding only fits a document
 * thin enough to take it, so "spiral everything" can legitimately half
 * succeed, and the customer has to be told which file kept its own setting.
 */
export function updateAllCartItems(
  patch: CartBulkUpdate,
  signal?: AbortSignal,
): Promise<CartBulkResultOut> {
  return api.patch<CartBulkResultOut>(`${CART_PATH}/items`, patch, signal ? { signal } : undefined);
}

/**
 * Freeze the cart into the quote checkout consumes.
 *
 * The one moment a price stops being recomputed. Refused outright — 422, with
 * the offending filenames — if any file is still being checked or cannot be
 * printed, rather than quietly leaving it out of the order.
 */
export function createCheckoutQuote(signal?: AbortSignal): Promise<QuoteOut> {
  return api.post<QuoteOut>(
    `${CART_PATH}/checkout-quote`,
    undefined,
    signal ? { signal } : undefined,
  );
}

export function clearCart(signal?: AbortSignal): Promise<CartOut> {
  return api.delete<CartOut>(CART_PATH, signal ? { signal } : undefined);
}

/**
 * The cart, as server state.
 *
 * `staleTime: 0` because a cart is a price: the catalogue can move, a shop
 * discount can land, and a file can finish validating, all without this tab
 * doing anything. Re-reading on mount is how those are noticed.
 *
 * `enabled` IS NOT OPTIONAL IN PRACTICE, and the reason is worth stating.
 * The server answers "whose cart is this?" from the access token when there
 * is one and from the anonymous session cookie otherwise — so asking before
 * the session has finished bootstrapping asks it as a stranger. A signed-in
 * customer who reloads the page would be shown an EMPTY cart, and because
 * this endpoint creates a cart when it finds none, the wrong answer gets
 * written down. The caller passes its auth state, the same way
 * `useAddresses` takes it.
 */
export function useCart({ enabled = true }: { enabled?: boolean } = {}) {
  return useApiQuery<CartOut, ReturnType<typeof queryKeys.cart>>({
    queryKey: queryKeys.cart(),
    queryFn: ({ signal }) => fetchCart(signal),
    staleTime: 0,
    enabled,
  });
}
